// src/hooks/useModuleAccess.ts
//
// Shared feature-access resolution for the module navigation (the app sidebar
// and the landing overview). Mirrors the gating that used to live inline in
// LandingPage: sysadmins and dev/no-auth mode see everything; otherwise a
// feature id is enabled iff the catalog default OR an explicit tenant/user DB
// grant resolves to enabled for this user on this tenant.

import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from './useAuth';
import { adminApi } from '../services/adminApi';
import { isFeatureEnabled } from '../lib/featureCatalog';
import { hasAdminRole } from '../lib/sysadminRoles';

export function useModuleAccess() {
  const { user, claims, activeTenant } = useAuthContext();
  const userRoles: string[] = claims?.roles || [];
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;

  // Dev / no-auth fallback: no roles at all -> show everything.
  const showAll = userRoles.length === 0;
  const isSysadmin = hasAdminRole(userRoles);

  // Sysadmin / showAll skip the queries entirely (they see every module).
  const enabled = !!slug && !showAll && !isSysadmin;

  // Tenant feature rows: also fetched for sysadmins, for tenantHas().
  const featuresQ = useQuery({
    queryKey: ['modaccess', 'features', slug],
    queryFn: () => adminApi.getAllFeatures(slug ?? undefined),
    enabled: !!slug && !showAll,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });
  const effectiveQ = useQuery({
    queryKey: ['modaccess', 'effective', slug, user?.id],
    queryFn: () => adminApi.getEffectiveFeatures(user!.id, slug ?? undefined),
    enabled: enabled && !!user?.id,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });
  const overridesQ = useQuery({
    queryKey: ['modaccess', 'overrides', slug, user?.id],
    queryFn: () => adminApi.getAllUserOverrides(user!.id, slug ?? undefined),
    enabled: enabled && !!user?.id,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });

  const effectiveSet = new Set(effectiveQ.data ?? []);
  const dbFeatureKeys = new Set((featuresQ.data ?? []).map((f) => f.featureKey));
  const userOverrides = new Map<string, boolean>(
    (overridesQ.data ?? []).map((o) => [o.featureKey, o.isEnabled]),
  );

  const loading = featuresQ.isLoading || effectiveQ.isLoading || overridesQ.isLoading;
  const errored = !!(featuresQ.error || effectiveQ.error || overridesQ.error);

  /**
   * Is a feature id visible for this user on this tenant?
   * `sysadminOnly` gates a module to admins regardless of catalog defaults.
   */
  const canAccess = (featureId: string, opts?: { sysadminOnly?: boolean }): boolean => {
    if (showAll) return true;
    if (opts?.sysadminOnly) return isSysadmin;
    if (isSysadmin) return true;
    if (loading) return false;
    if (errored) return isFeatureEnabled(featureId, new Set(), new Set(), new Map());
    return isFeatureEnabled(featureId, effectiveSet, dbFeatureKeys, userOverrides);
  };

  /**
   * Is a feature switched on for the TENANT (ignores the user's role and
   * overrides)? For flags that change how a module works for everyone, e.g.
   * financials.qbo swapping Financials to QuickBooks Online.
   */
  const tenantHas = (featureId: string): boolean => {
    const row = (featuresQ.data ?? []).find((f) => f.featureKey === featureId);
    return row ? row.isEnabled : isFeatureEnabled(featureId, new Set(), new Set(), new Map());
  };

  return { canAccess, tenantHas, loading, isSysadmin, showAll };
}
